import { useReducedMotion } from "framer-motion";
import { useTheme } from "../ThemeContext";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import ShinyText from "../reactbits/ShinyText";
import { DemoRequestDialog } from "@/components/DemoRequestDialog";
import { trustBadges } from "../../config/providerProof";
import { BadgeCheck, BookOpenCheck, ClipboardCheck, ShieldCheck, UserCheck, Lock } from "lucide-react";
import PinnedCardStack from "./PinnedCardStack";
import { secondaryButtonClass } from "./buttonStyles";
import { AccessGraphic, ControlGraphic, HipaaGraphic, KnowledgeGraphic, WorkspaceGraphic } from "./OversightGraphics";

const PRIVACY_URL = "https://web.medical.quilt.gg/legal/privacy";
const EULA_URL = "https://web.medical.quilt.gg/legal/eula";

// Teal from the Quilt bird logo
const TEAL = "#17a1a8";
const TEAL_LIGHT = "#3dc7cc";

const pillars = [
    {
        title: "You stay in control",
        description: "Nothing reaches a patient until your clinical team approves it. Review every scene, then approve, reject or send feedback.",
        icon: ClipboardCheck,
        graphic: <ControlGraphic />,
    },
    {
        title: "Grounded in your knowledge base",
        description: "Insights are based strictly on the clinical knowledge base and medical references you provide or approve of",
        icon: BookOpenCheck,
        graphic: <KnowledgeGraphic />,
    },
    {
        title: "Invite-only patient access",
        description: "Patients need a clinic-issued invite code to register and access Quilt",
        icon: UserCheck,
        graphic: <AccessGraphic />,
    },
    {
        title: "A secure workspace for your team",
        description: "Clinic administrators, therapists and support staff work together in a secure Quilt workspace.",
        icon: Lock,
        graphic: <WorkspaceGraphic />,
    },
];

// First three cards get swiped away; the fourth stays
const EXIT_RANGES: [number, number][] = [
    [0.08, 0.3],
    [0.36, 0.58],
    [0.64, 0.86],
];

export function SafetyTrust() {
    const { theme } = useTheme();
    const dark = theme === "dark";
    const reduceMotion = useReducedMotion();
    const muted = dark ? "text-slate-400" : "text-slate-600";
    const linkClass = dark
        ? "text-purple-400 hover:text-purple-300 underline underline-offset-4"
        : "text-purple-600 hover:text-purple-700 underline underline-offset-4";

    const header = (
        <div className="text-center">
            <Badge variant="outline" className="mb-3 px-4 py-2" style={{ background: `${TEAL}1a`, borderColor: `${TEAL}4d` }}>
                <ShieldCheck className="w-4 h-4 mr-2" style={{ color: TEAL }} />
                <ShinyText text="Safety, Security & Trust" speed={3} className="font-medium text-[#17a1a8]" />
            </Badge>
            <h2 className={`text-3xl md:text-5xl font-bold mb-4 ${dark ? "text-white" : "text-slate-900"}`}>
                Built for Safety
            </h2>
            <p className={`mx-auto hidden max-w-2xl text-base md:block md:text-lg ${muted}`}>
                Quilt acts as a digital extension of your clinic, so your expertise, not an algorithm, has the final say on
                everything your patients see.
            </p>
        </div>
    );

    const cardFace = (index: number) => {
        const { title, description, icon: Icon, graphic } = pillars[index];
        return (
            <div
                className={`relative flex h-full w-full flex-col justify-between overflow-hidden rounded-3xl border p-5 text-left shadow-2xl ${dark
                    ? "bg-slate-900 border-slate-700/60 shadow-black/50"
                    : "bg-white border-slate-200 shadow-slate-300/60"
                    }`}
            >
                <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl [clip-path:inset(0_round_1.5rem)]">
                    <div className="absolute -bottom-12 -right-12 h-48 w-48 rounded-full opacity-30 blur-3xl" style={{ background: TEAL }} />
                    <Icon className="absolute -bottom-6 -right-6 h-40 w-40 opacity-[0.05]" style={{ color: TEAL }} />
                </div>
                <div className="relative min-h-0 flex-1">
                    {graphic}
                    <span className={`absolute right-0 top-0 text-sm font-semibold tabular-nums ${dark ? "text-slate-500" : "text-slate-400"}`}>
                        0{index + 1} / 0{pillars.length}
                    </span>
                </div>
                <div className="relative mt-2">
                    <h3 className={`mb-1.5 text-xl font-bold ${dark ? "text-white" : "text-slate-900"}`}>{title}</h3>
                    <p className={`text-sm leading-relaxed ${muted}`}>{description}</p>
                </div>
            </div>
        );
    };

    const closing = (
        <>
            {trustBadges.length > 0 && (
                <ul className="mt-2 flex flex-wrap justify-center gap-3">
                    {trustBadges.map(({ label, detail, href }) => {
                        const content = (
                            <>
                                <BadgeCheck className="w-5 h-5 shrink-0" style={{ color: TEAL }} />
                                <span className="font-medium">{label}</span>
                                {detail && <span className={`text-xs ${muted}`}>{detail}</span>}
                            </>
                        );
                        const className = `flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${dark
                            ? "bg-slate-900/50 border-slate-800 text-slate-200"
                            : "bg-white border-slate-200 text-slate-800"
                            }`;
                        return (
                            <li key={label}>
                                {href ? (
                                    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
                                        {content}
                                    </a>
                                ) : (
                                    <div className={className}>{content}</div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            <Card
                className={`relative mx-auto mt-8 max-w-4xl overflow-hidden border p-6 sm:p-10 md:mt-12 md:p-14 ${dark
                    ? "bg-gradient-to-br from-slate-900 via-[#17a1a8]/10 to-slate-900 border-[#17a1a8]/30"
                    : "bg-gradient-to-br from-white via-cyan-50 to-white border-[#17a1a8]/30"
                    }`}
            >
                <div className="flex flex-col items-center gap-6 text-center md:flex-row md:gap-12 md:text-left">
                    <div className="h-28 w-52 shrink-0 sm:h-32 sm:w-60 md:h-36 md:w-72">
                        <HipaaGraphic />
                    </div>
                    <div className="flex flex-col items-center md:items-start">
                        <h3 className={`mb-3 text-2xl font-bold md:text-3xl ${dark ? "text-white" : "text-slate-900"}`}>
                            Built for HIPAA compliance
                        </h3>
                        <p className={`mb-4 text-base leading-relaxed ${muted}`}>
                            Quilt acts as a Business Associate of your clinic. Protected health information is processed only
                            on your behalf, under a Business Associate Agreement and as applicable law allows.
                        </p>
                        <p className={`mb-6 text-sm ${muted}`}>
                            Read how we handle information in our{" "}
                            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>Privacy Policy</a> and{" "}
                            <a href={EULA_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>End User License Agreement</a>.
                        </p>
                        <p className={`mb-3 text-sm font-medium ${dark ? "text-slate-200" : "text-slate-800"}`}>Have security questions?</p>
                        <DemoRequestDialog triggerText="Schedule a Demo" source="provider_schedule_demo">
                            <Button variant="outline" size="lg" className={`h-auto min-h-11 whitespace-normal px-6 py-2.5 text-base sm:px-8 sm:text-lg ${secondaryButtonClass(dark)}`}>
                                Talk to our team
                            </Button>
                        </DemoRequestDialog>
                    </div>
                </div>
            </Card>
        </>
    );

    // Reduced motion: a plain grid instead of the scroll-driven deck
    if (reduceMotion) {
        return (
            <div className="w-full max-w-6xl mx-auto py-10 md:py-24 px-4">
                <div className="mb-6 md:mb-14">{header}</div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {pillars.map(({ title, description, icon: Icon }) => (
                        <Card key={title} className={`flex flex-col p-6 border ${dark ? "bg-slate-900/50 border-slate-800" : "bg-white border-slate-200"}`}>
                            <div className="w-12 h-12 mb-5 rounded-xl flex items-center justify-center" style={{ background: `${TEAL}1a`, color: TEAL }}>
                                <Icon className="w-6 h-6" />
                            </div>
                            <h3 className={`text-lg font-semibold mb-2 ${dark ? "text-white" : "text-slate-900"}`}>{title}</h3>
                            <p className={`text-sm leading-relaxed ${muted}`}>{description}</p>
                        </Card>
                    ))}
                </div>
                <div className="mt-6 md:mt-10">{closing}</div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-6xl mx-auto px-4">
            <PinnedCardStack
                count={pillars.length}
                header={header}
                renderCard={cardFace}
                exitRanges={EXIT_RANGES}
                trackVh={260}
                fit
            />
            <div className="pb-10 md:pb-24">{closing}</div>
        </div>
    );
}

export default SafetyTrust;
